import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  Email as EmailIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ReceiptLong as ReceiptLongIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../config';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

interface Usuario {
  id: number;
  email: string;
  nombre: string;
  rol: string;
  medico_id: number;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isScrolled, setIsScrolled] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ nombre: '', email: '', password: '', rol: 'user', medico_id: 1 });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<Usuario | null>(null);

  // Obtener rol del usuario desde el token
  let userRole = '';
  let userName = '';
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const decoded: any = jwtDecode(token);
      userRole = decoded.rol || decoded.role || '';
      userName = decoded.nombre || '';
    } catch (e) {
      userRole = '';
      userName = '';
    }
  }

  useEffect(() => {
    // Decodificar token y establecer currentUserId
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        setCurrentUserId(decoded.sub); // Email como identificador temporal
      } catch (e) {
        setCurrentUserId(null);
      }
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleOpenCreateUser = async () => {
    setOpenCreateUser(true);
    setNewUser({ nombre: '', email: '', password: '', rol: 'user', medico_id: 1 });
    setCreateError('');
    setCreateSuccess('');
    await fetchUsuarios();
  };

  const handleCloseCreateUser = () => setOpenCreateUser(false);

  const fetchUsuarios = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_BASE_URL}/usuarios`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsuarios(data);
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    // Encontrar el usuario a borrar para mostrar su información
    const user = usuarios.find(u => u.id === userId);
    if (user) {
      setUserToDelete(user);
      setDeleteDialogOpen(true);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/usuarios/${userToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCreateSuccess('Usuario borrado exitosamente');
        await fetchUsuarios();
      } else {
        const error = await response.json();
        setCreateError(error.detail || 'Error al borrar usuario');
      }
    } catch (error) {
      setCreateError('Error al borrar usuario');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleCreateUser = async () => {
    setCreating(true);
    setCreateError('');
    setCreateSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/crear-usuario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newUser)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error al crear usuario');
      setCreateSuccess('Usuario creado exitosamente');
      setNewUser({ nombre: '', email: '', password: '', rol: 'user', medico_id: 1 });
      await fetchUsuarios();
    } catch (e: any) {
      setCreateError(e.message || 'Error al crear usuario');
    } finally {
      setCreating(false);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Pacientes', icon: <PeopleIcon />, path: '/pacientes' },
    { 
      text: location.pathname.includes('/pacientes/agregar') && location.search.includes('id=') ? 'Editar Paciente' : 'Agregar Paciente', 
      icon: location.pathname.includes('/pacientes/agregar') && location.search.includes('id=') ? <EditIcon /> : <AddIcon />, 
      path: '/pacientes/agregar',
      onClick: async () => {
        navigate('/pacientes/agregar');
        await new Promise(resolve => setTimeout(resolve, 100));
        window.dispatchEvent(new CustomEvent('limpiarFormularioPaciente'));
      }
    },
    { text: 'Agenda', icon: <CalendarIcon />, path: '/agenda' },
    { text: 'Emails', icon: <EmailIcon />, path: '/email' },
    { text: 'Facturación', icon: <ReceiptLongIcon />, path: '/facturacion', disabled: true },
    { text: 'Planes Alimentarios', icon: <RestaurantIcon />, path: '/planes', disabled: true },
    { text: 'Configuración', icon: <SettingsIcon />, path: '/facturacion-config' },
  ];

  const drawer = (
    <div style={{height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
      <div>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            p: 3,
            background: '#e0e0e0',
            borderRadius: '0 0 20px 20px',
            mb: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <img 
            src="/GPA.png" 
            alt="Logo" 
            style={{ 
              width: '150px', 
              height: '150px',
              objectFit: 'contain',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }} 
          />
        </Box>
        <List sx={{ px: 2 }}>
          {menuItems.map((item) => {
            const isSelected = location.pathname === item.path || 
                             (item.path === '/pacientes' && location.pathname.startsWith('/pacientes/') && !location.pathname.includes('/agregar')) ||
                             (item.path === '/dashboard' && location.pathname === '/dashboard');
            return (
              <ListItem
                component={item.disabled ? 'div' : 'button'}
                key={item.text}
                onClick={() => {
                  if (item.disabled) return;
                  if (item.onClick) {
                    item.onClick();
                  } else {
                    if (item.path === '/pacientes' && location.pathname.startsWith('/pacientes/') && !location.pathname.includes('/agregar')) {
                      window.location.href = '/pacientes';
                    } else {
                      navigate(item.path, { state: { from: 'menu' } });
                    }
                  }
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: '12px',
                  mb: 1,
                  backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.15)' : 'transparent',
                  border: isSelected ? '1px solid rgba(25, 118, 210, 0.5)' : '1px solid transparent',
                  transition: 'all 0.2s ease-in-out',
                  opacity: item.disabled ? 0.5 : 1,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: item.disabled ? 'transparent' : (isSelected ? 'rgba(25, 118, 210, 0.18)' : 'rgba(0, 0, 0, 0.06)'),
                    transform: item.disabled ? 'none' : 'translateX(8px)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: item.disabled ? 'rgba(0, 0, 0, 0.38)' : (isSelected ? 'rgba(25, 118, 210, 1)' : 'rgba(0, 0, 0, 0.8)'),
                    transition: 'all 0.2s ease-in-out',
                  },
                  '& .MuiListItemText-primary': {
                    color: item.disabled ? 'rgba(0, 0, 0, 0.38)' : (isSelected ? 'rgba(25, 118, 210, 1)' : 'rgba(0, 0, 0, 0.8)'),
                    fontWeight: isSelected ? 600 : 400,
                    transition: 'all 0.2s ease-in-out',
                  },
                  '&:hover .MuiListItemIcon-root': {
                    transform: item.disabled ? 'none' : 'scale(1.1)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}>{item.icon}</ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    letterSpacing: '0.3px'
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </div>
      <Box sx={{ px: 2, pb: 3 }}>
        {userRole === 'admin' && (
          <>
            <ListItem
              button
              onClick={handleOpenCreateUser}
              sx={{
                borderRadius: '12px',
                mb: 1,
                backgroundColor: 'transparent',
                border: '1px solid transparent',
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.06)',
                  transform: 'translateX(8px)',
                },
                '& .MuiListItemIcon-root': {
                  color: 'rgba(0, 0, 0, 0.8)',
                  transition: 'all 0.2s ease-in-out',
                },
                '& .MuiListItemText-primary': {
                  color: 'rgba(0, 0, 0, 0.8)',
                  fontWeight: 400,
                  transition: 'all 0.2s ease-in-out',
                },
                '&:hover .MuiListItemIcon-root': {
                  transform: 'scale(1.1)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 45 }}>
                <AddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Crear Usuario"
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  letterSpacing: '0.3px'
                }}
              />
            </ListItem>
            <ListItem
              button
              onClick={() => navigate('/pacientes-eliminados')}
              sx={{
                borderRadius: '12px',
                mb: 1,
                backgroundColor: location.pathname === '/pacientes-eliminados' ? 'rgba(25, 118, 210, 0.15)' : 'transparent',
                border: location.pathname === '/pacientes-eliminados' ? '1px solid rgba(25, 118, 210, 0.5)' : '1px solid transparent',
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: location.pathname === '/pacientes-eliminados' ? 'rgba(25, 118, 210, 0.18)' : 'rgba(0, 0, 0, 0.06)',
                  transform: 'translateX(8px)',
                },
                '& .MuiListItemIcon-root': {
                  color: location.pathname === '/pacientes-eliminados' ? 'rgba(25, 118, 210, 1)' : 'rgba(0, 0, 0, 0.8)',
                  transition: 'all 0.2s ease-in-out',
                },
                '& .MuiListItemText-primary': {
                  color: location.pathname === '/pacientes-eliminados' ? 'rgba(25, 118, 210, 1)' : 'rgba(0, 0, 0, 0.8)',
                  fontWeight: location.pathname === '/pacientes-eliminados' ? 600 : 400,
                  transition: 'all 0.2s ease-in-out',
                },
                '&:hover .MuiListItemIcon-root': {
                  transform: 'scale(1.1)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 45 }}>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Pacientes Eliminados"
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  letterSpacing: '0.3px'
                }}
              />
            </ListItem>
          </>
        )}
        <Dialog open={openCreateUser} onClose={handleCloseCreateUser} maxWidth="md" fullWidth>
          <DialogTitle>Gestión de Usuarios</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Crear nuevo usuario</Typography>
                <TextField
                  label="Nombre"
                  value={newUser.nombre}
                  onChange={e => setNewUser({ ...newUser, nombre: e.target.value })}
                  fullWidth
                  margin="normal"
                  size="small"
                />
                <TextField
                  label="Email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  fullWidth
                  margin="normal"
                  size="small"
                />
                <TextField
                  label="Contraseña"
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  fullWidth
                  margin="normal"
                  size="small"
                />
                <TextField
                  select
                  label="Rol"
                  value={newUser.rol}
                  onChange={e => setNewUser({ ...newUser, rol: e.target.value })}
                  fullWidth
                  margin="normal"
                  size="small"
                >
                  <MenuItem value="user">Usuario</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Clínica"
                  value={newUser.medico_id}
                  onChange={e => setNewUser({ ...newUser, medico_id: Number(e.target.value) })}
                  fullWidth
                  margin="normal"
                  size="small"
                  helperText="ID de la clínica a la que pertenecerá el usuario"
                >
                  <MenuItem value={1}>Clínica 1</MenuItem>
                  <MenuItem value={2}>Clínica 2</MenuItem>
                  <MenuItem value={3}>Clínica 3</MenuItem>
                </TextField>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Usuarios existentes</Typography>
                {loadingUsers ? (
                  <Typography variant="body2" color="text.secondary">Cargando...</Typography>
                ) : (
                  <List dense>
                    {usuarios.map((usuario: any) => (
                      <ListItem key={usuario.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={usuario.nombre || usuario.email}
                          secondary={`${usuario.email} (${usuario.rol}) - Clínica: ${usuario.medico_id}`}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                        {userRole === 'admin' && usuario.email !== currentUserId && (
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleDeleteUser(usuario.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
            {createError && <Typography color="error" sx={{ mt: 1, fontSize: '0.875rem' }}>{createError}</Typography>}
            {createSuccess && <Typography color="success.main" sx={{ mt: 1, fontSize: '0.875rem' }}>{createSuccess}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateUser} color="inherit" size="small">Cancelar</Button>
            <Button onClick={handleCreateUser} color="primary" disabled={creating} variant="contained" size="small">
              {creating ? 'Creando...' : 'Crear'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog 
          open={deleteDialogOpen} 
          onClose={cancelDeleteUser} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
            fontWeight: 600
          }}>
            <DeleteIcon color="error" />
            Confirmar Borrado
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              ¿Estás seguro de que quieres borrar este usuario?
            </Typography>
            {userToDelete && (
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1, 
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {userToDelete.nombre}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userToDelete.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Rol: {userToDelete.rol} | Clínica: {userToDelete.medico_id}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="error" sx={{ mt: 2, fontStyle: 'italic' }}>
              Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={cancelDeleteUser} 
              variant="outlined" 
              size="medium"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmDeleteUser} 
              color="error" 
              variant="contained" 
              size="medium"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)'
              }}
            >
              Borrar Usuario
            </Button>
          </DialogActions>
        </Dialog>
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            borderRadius: '12px',
            mt: 1,
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            transition: 'all 0.2s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              border: '1px solid rgba(211, 47, 47, 0.3)',
              transform: 'translateX(8px)',
            },
            '& .MuiListItemIcon-root': {
              color: 'rgba(0, 0, 0, 0.8)',
              transition: 'all 0.2s ease-in-out',
            },
            '& .MuiListItemText-primary': {
              color: 'rgba(0, 0, 0, 0.8)',
              fontWeight: 400,
              transition: 'all 0.2s ease-in-out',
            },
            '&:hover .MuiListItemIcon-root': {
              color: 'rgba(211, 47, 47, 0.9)',
              transform: 'scale(1.1)',
            },
            '&:hover .MuiListItemText-primary': {
              color: 'rgba(211, 47, 47, 0.9)',
              fontWeight: 500,
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 45 }}>
            <PowerSettingsNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Cerrar sesión"
            primaryTypographyProps={{
              fontSize: '0.95rem',
              letterSpacing: '0.3px'
            }}
          />
        </ListItem>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transform: isScrolled ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: 1200 // Asegurar que esté por debajo de los modales de composición
        }}
      >
        <Toolbar sx={{ 
          minHeight: '70px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2, 
                display: { sm: 'none' },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              noWrap 
              component="div"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.3px',
                color: '#ffffff',
                fontSize: '1.25rem',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              Gestión de Planes Alimentarios
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            alignSelf: { xs: 'flex-start', sm: 'center' },
            mt: { xs: 1, sm: 0 },
            mr: { xs: -2, sm: 0 }
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: { xs: '0.75rem' }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {currentTime.toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </Box>
              <Box 
                component="span" 
                sx={{ 
                  px: { xs: 0, sm: 1 },
                  borderLeft: { xs: 'none', sm: '1px solid rgba(255,255,255,0.2)' },
                  borderRight: { xs: 'none', sm: '1px solid rgba(255,255,255,0.2)' }
                }}
              >
                {currentTime.toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </Box>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
              boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
              boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 